<script setup lang="ts">

import {defineEmits, ref} from "vue";
import {useContentStore} from "../store/content";
import TooltipComponent from "./TooltipComponent.vue";

const emit = defineEmits(["toggle"])

const contentItem = useContentStore();
const embed = ref(false)
const embedUrl = ref(null)
const embedCode = ref(null)
const autoplay = ref(null)
const embedCodeText = ref('')

const urls = {
    facebook: new URL(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(contentItem.page_url)}&t=${encodeURIComponent(contentItem.title)}`),
    x: new URL(`https://x.com/intent/post?url=${encodeURIComponent(contentItem.page_url)}&text=${encodeURIComponent(contentItem.title)}`),
    linkedin: new URL(`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(contentItem.page_url)}&title=${encodeURIComponent(contentItem.title)}`),
    email: new URL(`mailto:?body=${encodeURIComponent(`Watch ${contentItem.title} on TrillerTV: ${contentItem.page_url}`)}&subject=${encodeURIComponent(contentItem.title)}`),
    pinterest: new URL(`https://pinterest.com/pin/create/button/?url=${encodeURIComponent(contentItem.page_url)}&description=${encodeURIComponent(contentItem.title)}`)
}

function onToggleEmbed(){
    embed.value = !embed.value
}

function buildEmbedCode(){
    const eEl = document.createElement("div");
    eEl.setAttribute("data-id", contentItem.itemId)
    eEl.setAttribute("class", "kdv-embed")
    eEl.setAttribute("data-type", "v")
    if(autoplay.value && (autoplay.value as HTMLInputElement).checked){
        eEl.setAttribute("data-ap", "true")
    }else{
        eEl.setAttribute("data-ap", "false")
    }
    const embedScr = document.createElement("script")
    embedScr.setAttribute("src", "https://www.trillertv.com/embed.1.js")

    embedCodeText.value = `${eEl.outerHTML}\n${embedScr.outerHTML}`;
}

function onCopyClick() {

    if (embed.value){
        if (embedCode.value){
            (embedCode.value as HTMLTextAreaElement).select()
            try {
                navigator.clipboard.writeText((embedCode.value as HTMLTextAreaElement).value);
            }catch(err){
                console.log('Can\'t copy to clipboard');
            }
        }
    }else{
        if (embedUrl.value){
            (embedUrl.value as HTMLInputElement).select()
            try {
                navigator.clipboard.writeText((embedUrl.value as HTMLInputElement).value);
            }catch(err){
                console.log('Can\'t copy to clipboard');
            }
        }
    }
}

buildEmbedCode()
</script>

<template>
    <div class="fixed inset-0 z-[999] grid h-screen w-screen place-items-center">
        <div class="bg-mono-ns-100 rounded-3xl w-[359px] xl:w-[569px] flex flex-col p-2 xl:p-4 font-sans gap-6">
            <div class="flex">
                <button type="button" class="p-2.5 justify-self-start" :class="{'hidden': !embed}" @click="onToggleEmbed">
                    <svg class="w-6 h-6" width="24" height="24">
                        <use xlink:href="#icon-left"></use>
                    </svg>
                </button>
                <button type="button" class="p-2.5 text-mono-ps-200 ml-auto" @click="emit('toggle')">
                    <svg class="w-6 h-6" width="24" height="24">
                        <use xlink:href="#icon-close"></use>
                    </svg>
                </button>
            </div>
            <h3 class="text-h3m xl:text-h3 font-bold text-mono-white text-center px-2 xl:px-4 -mt-4">Share</h3>
            <div class="flex flex-col gap-6 px-2 xl:px-4" :class="{'hidden': embed}">
                <ul class="grid grid-cols-3 xl:grid-cols-6 justify-items-center gap-4 text-mono-white text-p6m py-6">
                    <li class="">
                        <a :href="urls.facebook.toString()" target="_blank" title="Facebook" class="flex flex-col items-center group relative">
                            <svg class="w-11 h-11 bg-mono-ns-200 rounded-full p-2.5 block" width="24" height="24" viewBox="0 0 48 48">
                                <use xlink:href="#icon-fb"></use>
                            </svg>
                            <span class="md:hidden py-1">Facebook</span>
                            <TooltipComponent label="Facebook"></TooltipComponent>
                        </a>
                    </li>
                    <li class="">
                        <a :href="urls.x.toString()" target="_blank" title="X" class="flex flex-col items-center group relative">
                            <svg class="w-11 h-11 bg-mono-ns-200 rounded-full p-2.5 block" width="24" height="24" viewBox="0 0 48 48">
                                <use xlink:href="#icon-x"></use>
                            </svg>
                            <span class="md:hidden py-1">X</span>
                            <TooltipComponent label="X"></TooltipComponent>
                        </a>
                    </li>
                    <li class="">
                        <a :href="urls.linkedin.toString()" target="_blank" title="Linkedin" class="flex flex-col items-center group relative">
                            <svg class="w-11 h-11 bg-mono-ns-200 rounded-full p-2.5 block" width="24" height="24" viewBox="0 0 48 48">
                                <use xlink:href="#icon-li"></use>
                            </svg>
                            <span class="md:hidden py-1">Linkedin</span>
                            <TooltipComponent label="Linkedin"></TooltipComponent>
                        </a>

                    </li>
                    <li class="">
                        <a :href="urls.pinterest.toString()" target="_blank" title="Pinterest" class="flex flex-col items-center group relative">
                            <svg class="w-11 h-11 bg-mono-ns-200 rounded-full p-2.5 block" width="24" height="24" viewBox="0 0 24 24">
                                <use xlink:href="#icon-pin"></use>
                            </svg>
                            <span class="md:hidden py-1">Pinterest</span>
                            <TooltipComponent label="Pinterest"></TooltipComponent>
                        </a>

                    </li>
                    <li class="">
                        <a :href="urls.email.toString()" target="_blank" title="Email" class="flex flex-col items-center group relative">
                            <svg class="w-11 h-11 bg-mono-ns-200 rounded-full p-2.5 block" width="24" height="24">
                                <use xlink:href="#icon-email"></use>
                            </svg>
                            <span class="md:hidden py-1">Email</span>
                            <TooltipComponent label="Email"></TooltipComponent>
                        </a>

                    </li>
                    <li class="">
                        <button type="button" title="Embed" class="flex flex-col items-center group relative" @click="onToggleEmbed">
                            <svg class="w-11 h-11 bg-mono-ns-200 rounded-full p-2.5 block" width="24" height="24">
                                <use xlink:href="#icon-embed"></use>
                            </svg>
                            <span class="md:hidden py-1">Embed</span>
                            <TooltipComponent label="Embed"></TooltipComponent>
                        </button>

                    </li>
                </ul>
                <input ref="embedUrl" class="bg-mono-white text-mono-ns-100 text-p5m px-4 py-2 rounded-3xl" type="text" :value="contentItem.page_url" readonly>
            </div>

            <div class="flex flex-col gap-6 px-2 xl:px-4" :class="{'hidden': !embed}">
                <textarea ref="embedCode" rows="3" class="bg-mono-white text-mono-ns-100 text-p5m px-4 py-2 rounded-3xl w-full" :value="embedCodeText"></textarea>
                <label class="inline-flex items-center cursor-pointer">
                    <span class="mr-2">Autoplay</span>
                    <input ref="autoplay" type="checkbox" value="" class="sr-only peer" @change="buildEmbedCode">
                    <span class="relative w-11 h-6 bg-mono-ps-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-sa-light"></span>
                </label>
            </div>

            <div class="flex flex-col px-2 xl:px-4">
                <button class="mb-8 xl:mb-6 cursor-pointer font-bold text-center btn rounded-2.4xl xl:rounded-3xl text-mono-ns-200 bg-mono-white hover:bg-mono-ps-100 min-w-fit xl:w-52 w-full relative self-center" @click="onCopyClick">Copy</button>
            </div>
        </div>
    </div>
</template>
